/**
 * SrPlaybackBar — Synth VR editor bottom bar.
 *
 * Two rows (SR only — the other editors keep the shared EditorPlaybackBar layout), SRVR-0185 AC5:
 *   [ transport pill ] [ hint ] [ song-info pill ]
 *   [ waveform, the full width of the bar          ]
 * The note-count / beat readout is removed; the song cover + name + artist now
 * lives in the right pill. The waveform (Timeline) is embedded in the centre.
 */

import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { getArtworkUrl } from '../../api/client';
import { transportStyle, transportBtn } from '../ui/editorSharedStyles';
import { color, font } from '../ui/theme';
import { IcTriRight } from '../ui/icons';
import SongTag from '../ui/SongTag';
import Timeline from './Timeline';
import SrHintBar from './SrHintBar';
import AudioBars from './AudioBars';
import { formatBeatOnStep, formatBeatReadout, formatClock } from './transportReadout';
import { isSynthVrView } from '../../store/editorSettings';

const fmtTime = (s: number) => {
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

export default function SrPlaybackBar() {
  const beatmap = useEditorStore(s => s.beatmap);
  // Only the play state re-renders the bar; the time and beat are written by TransportReadout through
  // refs (style rule 3: nothing subscribes to the playhead).
  const playing = useEditorStore(s => s.playback.playing);
  const setPlaying = useEditorStore(s => s.setPlaying);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);
  const setEditInfoOpen = useEditorStore(s => s.setEditInfoOpen);
  const edge = useEditorStore(s => s.interfaceSettings.waveformEdge ?? 'bottom');

  if (!beatmap) return null;

  return (
    // 3-column grid (transport · hint · song) over a full-width waveform row. The column widths live
    // in App.css (.sr-playbar, shared with the other editors' bars) so media queries can reshape them;
    // `sr-playbar--stacked` is what spans the waveform across all three.
    <div className={`sr-playbar sr-playbar--stacked${edge === 'bottom' ? '' : ' sr-playbar--wave-' + edge}`}>
      {/* Row 1, col 1 — Transport. Small right padding so the spectrum pill
          nestles into the container's right curve. */}
      <div className="sr-pb-pill sr-transport-pill" data-testid="sr-transport-pill" style={{ ...barPillStyle, paddingLeft: 6, paddingRight: 4 }}>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setCurrentTime(0); }}
          style={transportBtnRound}
          title="Rewind"
          data-no-tint-hover
          className="accent-fill-hover"
          onMouseEnter={roundHoverIn} onMouseLeave={roundHoverOut}
        >⏮</button>
        <button
          onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setPlaying(!playing); }}
          style={transportBtnRound}
          title="Play / Pause"
          data-no-tint-hover
          className={playing ? 'accent-fill-hover accent-fill-active' : 'accent-fill-hover'}
          onMouseEnter={roundHoverIn} onMouseLeave={roundHoverOut}
        >{playing ? '❚❚' : <IcTriRight size={14} />}</button>
        {/* Time and beat, centred between the buttons and the visualizer (SRVR-0185 AC6–AC7). */}
        <TransportReadout />
        <div className="sr-viz-pill" style={vizPillStyle}>
          <AudioBars width={56} height={24} />
        </div>
      </div>

      {/* Row 1, col 2 — the hint, between the two pills. */}
      <SrHintBar />

      {/* Row 1, col 3 — Song info. Small left padding so the round vinyl cover
          nestles into the pill's left curve. */}
      <div className="sr-pb-pill sr-song-pill" data-testid="sr-song-pill" style={{ ...barPillStyle, paddingLeft: 6 }}>
        <SongTag
          bare
          spinning={playing}
          coverUrl={beatmap.id ? getArtworkUrl(beatmap.id) : null}
          primary={beatmap.name?.trim() || '(untitled)'}
          secondary={beatmap.author?.trim() || 'Unknown artist'}
          duration={0}
          onClick={() => setEditInfoOpen(true)}
        />
      </div>

      {/* Row 2 — Waveform, the bar's full width. Clipping across clips the timeline's fixed
          right-edge controls on very narrow viewports instead of letting them spill past the edge;
          not clipping up and down lets a bookmark's name stand above the strip. */}
      {/* On the other three edges the strip leaves the bar (App.css: .sr-playbar--wave-*) and floats
          at that edge of the editing area, between the sidebars. */}
      <div className="sr-pb-wave" data-testid="sr-pb-wave" data-edge={edge} style={{ minWidth: 0, display: 'flex', overflowX: edge === 'bottom' ? 'clip' : 'visible', overflowY: 'visible' }}>
        <Timeline />
      </div>
    </div>
  );
}

/**
 * The current time to the millisecond over the song length, and the current beat over the step's
 * denominator. Written into the DOM on every store change that moves them, never through React state.
 */
function TransportReadout() {
  const timeRef = useRef<HTMLSpanElement>(null);
  const beatRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let lastTime = '', lastBeat = '';
    const write = (s: ReturnType<typeof useEditorStore.getState>) => {
      const time = `${formatClock(s.playback.currentTime)} / ${fmtTime(s.playback.duration)}`;
      const beat = `Beat ${isSynthVrView(s.activeView) ? formatBeatReadout(s.playback.currentBeat, s.scrollStep, s.playback.playing) : formatBeatOnStep(s.playback.currentBeat, s.scrollStep)}`;
      if (time !== lastTime && timeRef.current) { timeRef.current.textContent = time; lastTime = time; }
      if (beat !== lastBeat && beatRef.current) { beatRef.current.textContent = beat; lastBeat = beat; }
    };
    write(useEditorStore.getState());
    return useEditorStore.subscribe(write);
  }, []);
  return (
    <span style={readoutColumnStyle}>
      <span ref={timeRef} data-testid="sr-transport-time" style={timeReadoutStyle} />
      <span ref={beatRef} data-testid="sr-transport-beat" style={beatReadoutStyle} />
    </span>
  );
}

const readoutColumnStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25,
};

const barPillStyle: React.CSSProperties = {
  ...transportStyle,
  borderRadius: 999,
  gap: 10,
  height: 48,
  padding: '0 16px',
  alignItems: 'center',
};

const transportBtnRound: React.CSSProperties = {
  ...transportBtn,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s ease, color 0.15s ease',
};

// Hover for the round transport buttons: fill the container with the accent and
// flip the icon to the dark tint (matches the stepper arrows).
const roundHoverIn = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'oklch(0.62 calc(0.14 * var(--accent-c)) var(--accent-h))';
  e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))';
};
const roundHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  e.currentTarget.style.color = 'var(--accent)';
};

const timeReadoutStyle: React.CSSProperties = {
  color: color.ink,
  fontFamily: font.sans,
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const beatReadoutStyle: React.CSSProperties = {
  ...timeReadoutStyle,
  color: color.inkDim,
  fontSize: 10,
};

// Live-spectrum pill on the right of the playback bar. Sized to match the two
// transport buttons: same height (36, like the play button) and as wide as the
// two buttons together (36 + 10 gap + 36 = 82).
const vizPillStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 82,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};
